import { QueryClient } from '@tanstack/react-query';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { EXPORT_MAX_MONTHS } from '@repo/shared/dtos/analytics/ExportDto';
import { SessionDataDashboardDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { fetchMemberTrendsExport, fetchSessionExport } from '../../../hooks/analytics/useExport';
import { analyticsQueryKeys } from '../../../hooks/analytics/queryKeys';
import { BASE_URLS, CACHE_TIMES } from '../../../hooks/analytics/config';
import { createAnalyticsUrl, fetchAnalyticsData } from '../../../hooks/analytics/utils';
import { toDateParam } from './exportRange';

export type ExportDatasetKey =
  | 'dailySessions'
  | 'monthlyMembers'
  | 'sessionTotals'
  | 'topExperiences'
  | 'monthlySessions';

export type ExportDatasetGroup = 'perUnit' | 'totals';

export interface ExportDataset {
  key: ExportDatasetKey;
  group: ExportDatasetGroup;
  label: string;
  description: string;
  /** Wording when the export only ever covers one unit. */
  singleUnitDescription: string;
  fileBase: string;
  maxMonths: number;
}

export const EXPORT_DATASETS: ExportDataset[] = [
  {
    key: 'dailySessions',
    group: 'perUnit',
    label: 'Daily sessions',
    description: 'One row per unit per day: sessions and biosensor sessions',
    singleUnitDescription: 'One row per day: sessions and biosensor sessions',
    maxMonths: EXPORT_MAX_MONTHS.daily,
    fileBase: 'daily_sessions',
  },
  {
    key: 'monthlyMembers',
    group: 'perUnit',
    label: 'Monthly members',
    description: 'One row per unit per month: new, returning and total members, return rate',
    singleUnitDescription: 'One row per month: new, returning and total members, return rate',
    maxMonths: EXPORT_MAX_MONTHS.monthly,
    fileBase: 'monthly_members',
  },
  {
    key: 'sessionTotals',
    group: 'totals',
    label: 'Session totals',
    description: 'One row: total sessions and minutes',
    singleUnitDescription: 'One row: total sessions and minutes',
    maxMonths: EXPORT_MAX_MONTHS.monthly,
    fileBase: 'session_totals',
  },
  {
    key: 'topExperiences',
    group: 'totals',
    label: 'Top experiences',
    description: 'One row per experience: sessions, minutes and share of sessions',
    singleUnitDescription: 'One row per experience: sessions, minutes and share of sessions',
    maxMonths: EXPORT_MAX_MONTHS.monthly,
    fileBase: 'top_experiences',
  },
  {
    key: 'monthlySessions',
    group: 'totals',
    label: 'Monthly sessions',
    description: 'One row per month: sessions and minutes',
    singleUnitDescription: 'One row per month: sessions and minutes',
    maxMonths: EXPORT_MAX_MONTHS.monthly,
    fileBase: 'monthly_sessions',
  },
];

export interface ExportSelection {
  dataset: ExportDatasetKey;
  startDate: Date;
  endDate: Date;
  unitIds: number[];
}

export type ExportRow = object;

const formatMonth = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export function buildExportFileName(
  dataset: ExportDatasetKey,
  range: { startDate: Date; endDate: Date },
): string {
  const base = EXPORT_DATASETS.find(d => d.key === dataset)?.fileBase ?? 'export';
  const start = formatMonth(range.startDate);
  const end = formatMonth(range.endDate);
  return start === end ? `${base}_${start}` : `${base}_${start}_to_${end}`;
}

function toTotalsRows(data: SessionDataDashboardDto, dataset: ExportDatasetKey): ExportRow[] {
  if (dataset === 'sessionTotals') {
    // The *AllTime fields are totals for the requested range, not literally all time.
    const kpis = data.sessionMinutes;
    return kpis
      ? [{ total_sessions: kpis.totalSessionsAllTime, total_minutes: kpis.totalMinutesAllTime }]
      : [];
  }
  if (dataset === 'topExperiences') {
    return (data.topExperiencesAllTime ?? []).map(row => ({
      experience_title: row.experienceTitle,
      session_count: row.sessionCount,
      total_minutes: row.totalMinutes,
      percentage: row.percentage,
    }));
  }
  return (data.sessionTrends ?? []).map(row => ({
    // monthStart is a UTC month boundary; local getters would shift it into the previous month.
    month: new Date(row.monthStart).toISOString().slice(0, 7),
    session_count: row.sessionCount,
    total_minutes: row.totalMinutes,
  }));
}

export async function fetchExportRows(
  queryClient: QueryClient,
  selection: ExportSelection,
): Promise<ExportRow[]> {
  const params = {
    startDate: toDateParam(selection.startDate),
    endDate: toDateParam(selection.endDate),
    unitIds: selection.unitIds,
  };

  if (selection.dataset === 'dailySessions') {
    return fetchSessionExport(queryClient, params);
  }
  if (selection.dataset === 'monthlyMembers') {
    return fetchMemberTrendsExport(queryClient, params);
  }

  const filters: BaseAnalyticsFilter = {
    startDate: selection.startDate,
    endDate: selection.endDate,
    unitIds: selection.unitIds,
    experienceIds: [],
    granularity: 'month',
  };
  const dashboard = await queryClient.fetchQuery({
    queryKey: analyticsQueryKeys.sessions.dashboard(filters),
    queryFn: ({ signal }) =>
      fetchAnalyticsData<SessionDataDashboardDto>(
        createAnalyticsUrl(BASE_URLS.SESSIONS, '/dashboard', filters),
        signal,
      ),
    staleTime: CACHE_TIMES.STALE_TIME,
  });
  return toTotalsRows(dashboard, selection.dataset);
}
